/**
 * Chat Service
 * Handles API calls and WebSocket connection for real-time chat.
 */

import { getBackendUrl } from '../config';

const API_URL = getBackendUrl();

class ChatService {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
  }

  // ========== WebSocket Methods ==========

  connect(token) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return;
    }

    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    this.ws = new WebSocket(`${wsUrl}/api/chat/ws?token=${token}`);

    this.ws.onopen = () => {
      console.log('[Chat] WebSocket connected');
      this.reconnectAttempts = 0;
      this.emit('connected');
      
      // Start ping interval
      this.pingInterval = setInterval(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ action: 'ping' }));
        }
      }, 30000);
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[Chat] Received:', data.type);
        this.emit(data.type, data);
        this.emit('message', data);
      } catch (e) {
        console.error('[Chat] Failed to parse message:', e);
      }
    };

    this.ws.onclose = (event) => {
      console.log('[Chat] WebSocket closed:', event.code, event.reason);
      clearInterval(this.pingInterval);
      this.emit('disconnected');
      
      // Attempt reconnect
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        setTimeout(() => {
          console.log(`[Chat] Reconnecting... (attempt ${this.reconnectAttempts})`);
          this.connect(token);
        }, this.reconnectDelay * this.reconnectAttempts);
      }
    };

    this.ws.onerror = (error) => {
      console.error('[Chat] WebSocket error:', error);
      this.emit('error', error);
    };
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    clearInterval(this.pingInterval);
  }

  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  subscribeToConversation(conversationId) {
    this.send({ action: 'subscribe', conversation_id: conversationId });
  }

  unsubscribeFromConversation(conversationId) {
    this.send({ action: 'unsubscribe', conversation_id: conversationId });
  }

  sendTyping(conversationId, userName, isTyping) {
    this.send({
      action: 'typing',
      conversation_id: conversationId,
      user_name: userName,
      is_typing: isTyping
    });
  }

  // Event handling
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
    return () => this.off(event, callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => callback(data));
    }
  }

  // ========== REST API Methods ==========

  async getAuthHeaders() {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  async createConversation(subject, initialMessage, relatedType = null, relatedId = null) {
    const response = await fetch(`${API_URL}/api/chat/conversations`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({
        subject,
        initial_message: initialMessage,
        related_type: relatedType,
        related_id: relatedId
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to create conversation');
    }
    
    return response.json();
  }

  async getConversations(status = null) {
    const url = new URL(`${API_URL}/api/chat/conversations`);
    if (status) {
      url.searchParams.append('status', status);
    }
    
    const response = await fetch(url, {
      headers: await this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch conversations');
    }
    
    return response.json();
  }

  async getConversation(conversationId) {
    const response = await fetch(`${API_URL}/api/chat/conversations/${conversationId}`, {
      headers: await this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch conversation');
    }
    
    return response.json();
  }

  async sendMessage(conversationId, content, messageType = 'text') {
    const response = await fetch(`${API_URL}/api/chat/conversations/${conversationId}/messages`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ content, message_type: messageType })
    });
    
    if (!response.ok) {
      throw new Error('Failed to send message');
    }
    
    return response.json();
  }

  async joinConversation(conversationId) {
    const response = await fetch(`${API_URL}/api/chat/conversations/${conversationId}/join`, {
      method: 'POST',
      headers: await this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to join conversation');
    }
    
    return response.json();
  }

  async closeConversation(conversationId) {
    const response = await fetch(`${API_URL}/api/chat/conversations/${conversationId}/close`, {
      method: 'POST',
      headers: await this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to close conversation');
    }
    
    return response.json();
  }

  async getUnreadCount() {
    const response = await fetch(`${API_URL}/api/chat/unread-count`, {
      headers: await this.getAuthHeaders()
    });
    
    if (!response.ok) {
      return { unread_count: 0 };
    }
    
    return response.json();
  }

  async markMessageRead(conversationId, messageId) {
    const response = await fetch(`${API_URL}/api/chat/conversations/${conversationId}/messages/${messageId}/read`, {
      method: 'POST',
      headers: await this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to mark message as read');
    }
    
    return response.json();
  }

  async markAllRead(conversationId) {
    const response = await fetch(`${API_URL}/api/chat/conversations/${conversationId}/read-all`, {
      method: 'POST',
      headers: await this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to mark messages as read');
    }
    
    return response.json();
  }
}

// Export singleton instance
export const chatService = new ChatService();
export default chatService;
